import { useEffect } from "react";
export default function Terms(){
  useEffect(()=>{ document.title = "Terms & Conditions | MikDok"; },[]);
  return (
    <main style={{maxWidth:900,margin:"48px auto",padding:"0 22px",lineHeight:1.7,color:"#0f172a",fontFamily:"system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif"}}>
      <h1 style={{fontSize:"2rem",marginBottom:8,fontWeight:800}}>Terms & Conditions</h1>
      <p style={{color:"#64748b"}}><strong>Effective date:</strong> 13 November 2025</p>

      <p>
        Welcome to <strong>MikDok</strong> (“<strong>we</strong>”, “<strong>us</strong>”, “<strong>our</strong>”).  
        These Terms and Conditions (“<strong>Terms</strong>”) govern your access to and use of
        <a href="https://mikdok.com" target="_blank" rel="noreferrer"> mikdok.com</a>,
        its mobile versions, and any related digital services (collectively, the “<strong>Platform</strong>”).
        MikDok is currently operated by its founders — pending formal company registration in the Republic of Albania.
      </p>

      <p>
        By creating an account or using the Platform you agree to these Terms, our <a href="/privacy">Privacy Policy</a>, and our
        <a href="/cookies">Cookie Policy</a>.  If you do not agree, please do not use the Platform.
      </p>

      <h2>1. Eligibility & Accounts</h2>
      <ul>
        <li>You must be at least 16 years old and legally capable of entering a binding agreement.</li>
        <li>Information you provide (name, license, clinic data, email, etc.) must be truthful and kept current.</li>
        <li>You are responsible for safeguarding credentials and for all activity under your account.</li>
        <li>We may suspend, limit, or terminate accounts that violate these Terms, applicable laws, or professional ethics.</li>
      </ul>

      <h2>2. Role of MikDok</h2>
      <p>
        MikDok is a technology intermediary designed to connect patients, healthcare professionals, and clinics.
        We <strong>do not</strong> provide medical treatment, diagnose conditions, or substitute for professional judgment.
        Any medical or dental service is delivered exclusively by the licensed professional or clinic you choose.
      </p>
      <p>
        Our role is limited to: (a) publishing verified professional and clinic profiles;
        (b) enabling communication, scheduling, and lead management; and (c) hosting educational or community content.
      </p>

      <h2>3. Use of the Platform</h2>
      <ul>
        <li>You agree to use the Platform lawfully and respectfully.</li>
        <li>You must not upload malicious code, harass others, impersonate, mislead, or scrape data.</li>
        <li>Professionals must comply with medical-advertising and confidentiality regulations in their jurisdiction.</li>
        <li>We reserve the right to moderate, edit, or remove any content violating these Terms or the rights of others.</li>
      </ul>

      <h2>4. Professional Listings & Verification</h2>
      <p>
        Each professional or clinic is responsible for the accuracy of profile information, licenses, qualifications,
        and offered services.  We may request documentation for verification but cannot guarantee completeness or authenticity.
        Verification status (“verified”, “pending”, etc.) is informational only.
      </p>

      <h2>5. Bookings, Messaging & Future Telehealth</h2>
      <p>
        Booking requests made through MikDok are transmitted to the selected professional or clinic.
        Confirmation, cancellation, treatment scope, and pricing are agreed directly between patient and provider.
      </p>
      <p>
        Messaging tools on the Platform are intended for coordination, not emergency or diagnostic communication.
        In an emergency, call your local emergency number immediately.
      </p>
      <p>
        We may in the future offer secure video-consultation (“telehealth”) features.  Those features will
        require identity verification, encryption, and explicit patient consent before activation.
      </p>

      <h2>6. Payments & Third-Party Processors</h2>
      <p>
        MikDok may later integrate payment processing (e.g., Stripe, PayPal, bank gateways).  
        Such payments will be handled by licensed third-party processors that comply with PCI-DSS standards.
        We will not store full card numbers.  Applicable processing fees and terms will be displayed when launched.
      </p>
      <p>
        Professionals remain responsible for issuing invoices, paying taxes, and complying with national health-sector fiscal rules.
      </p>

      <h2>7. Reviews, Ratings & Social Features</h2>
      <ul>
        <li>Patients may submit reviews based on genuine experiences; reviews must be factual and respectful.</li>
        <li>We may remove or anonymize reviews that are fraudulent, defamatory, or violate privacy laws.</li>
        <li>Professionals may not post self-reviews or offer incentives for positive feedback.</li>
      </ul>

      <h2>8. Intellectual Property</h2>
      <p>
        The MikDok name, logo, design system, and software code are proprietary.
        All rights are reserved by the Founders or their licensors.
        You may not copy, distribute, decompile, or reuse parts of the Platform except under permitted-use rights.
      </p>
      <p>
        Users retain ownership of their own uploaded content (e.g., posts, research, media) but grant MikDok a
        non-exclusive, worldwide, royalty-free license to host, display, and distribute such content for Platform operation and promotion.
      </p>

      <h2>9. Privacy & Data Protection</h2>
      <p>
        We process personal data according to the <a href="/privacy">Privacy Policy</a> and EU GDPR standards.
        Data is stored primarily in the European Union using Firebase / Google Cloud infrastructure with encryption at rest and in transit.
      </p>
      <p>
        You may request data access, correction, or deletion by contacting <a href="mailto:contact@mikdok.al">contact@mikdok.al</a>.
      </p>

      <h2>10. Third-Party Tools & APIs</h2>
      <ul>
        <li>Google Maps API – used for clinic location display and distance estimation.</li>
        <li>Firebase – used for authentication, storage, Firestore database, and analytics.</li>
        <li>Optional integrations (e.g., Brevo email, Cloud Messaging) – used for communication and notifications.</li>
      </ul>
      <p>
        Each provider has its own privacy policy and terms; MikDok is not liable for their independent operations.
      </p>

      <h2>11. Community & Educational Content</h2>
      <p>
        The Platform may host articles, posts, or discussions intended for general informational purposes.
        Such content is <strong>not medical advice</strong> and should not replace professional consultation.
      </p>

      <h2>12. Disclaimers</h2>
      <p>
        The Platform and all information are provided “as is” and “as available”.
        We make no warranties—express or implied—regarding accuracy, completeness, availability, or fitness for a particular purpose.
        We do not endorse or guarantee any professional’s qualifications, insurance coverage, or treatment results.
      </p>

      <h2>13. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, MikDok, its founders, employees, and partners shall not be liable for:
        (a) indirect, incidental, consequential, or punitive damages; (b) loss of data, revenue, or goodwill;
        (c) errors or interruptions of service; or (d) damages arising from professional negligence or patient–doctor relationships.
      </p>

      <h2>14. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless MikDok, the Founders, and affiliates against claims, damages, or costs
        resulting from your misuse of the Platform, your content, or your violation of these Terms or law.
      </p>

      <h2>15. Suspension, Termination & Account Deletion</h2>
      <p>
        We may suspend or terminate accounts for policy violations, security concerns, or legal obligations.
        You may delete your account via settings or by written request; deletion will follow applicable data-retention rules.
      </p>

      <h2>16. Notifications & Communication</h2>
      <p>
        By using MikDok you consent to receive system or legal notifications electronically (email or in-app).
        Marketing communications are optional and require explicit opt-in consent under GDPR.
      </p>

      <h2>17. Governing Law & Jurisdiction</h2>
      <p>
        These Terms are governed by the laws of the Republic of Albania.  
        Disputes shall be subject to the exclusive jurisdiction of the courts of Tirana,
        unless mandatory EU consumer-protection laws provide otherwise.
        Parties agree to attempt amicable resolution before litigation.
      </p>

      <h2>18. Changes to These Terms</h2>
      <p>
        We may revise these Terms from time to time.
        Updated versions will appear on <a href="https://mikdok.com">mikdok.com</a> with a new “Effective date”.
        Significant changes will be notified via email or in-app banner.
      </p>

      <h2>19. Contact & Operator Information</h2>
      <p>
        Email: <a href="mailto:contact@mikdok.al">contact@mikdok.al</a><br/>
       
        Jurisdiction: Republic of Albania (EU GDPR Zone)
      </p>

      <p style={{color:"#64748b",marginTop:24}}>
        © {new Date().getFullYear()} MikDok — All rights reserved. Use of this Platform implies acceptance of these Terms.
      </p>
    </main>
  );
}
